/**
 * FatturaRow — F-011 (rescoped). Riga della coda "fatture da emettere":
 * data, cliente + dati fatturazione, tipo, importo, riferimento, stato,
 * azione "Segna come fatturata" con numero fattura opzionale.
 * Presentazionale: callback e flag pending dall'esterno.
 */
import { useState } from 'react';
import { FATTURA_TIPO_LABELS, FATTURA_STATO_LABELS } from '@app/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { FatturaView, MarkFatturataBody } from '../lib/fatture-client';

interface FatturaRowProps {
  fattura: FatturaView;
  onMarkFatturata: (body: MarkFatturataBody) => void;
  isSaving?: boolean;
}

const EURO_FORMAT = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const DATE_FORMAT = new Intl.DateTimeFormat('it-IT', { dateStyle: 'short' });

function clienteLabel(fattura: FatturaView): string {
  const bp = fattura.billingSnapshot;
  if (bp.ragioneSociale) return bp.ragioneSociale;
  const nome = [bp.firstName, bp.lastName].filter(Boolean).join(' ').trim();
  return nome || fattura.account.name || fattura.account.email;
}

export function FatturaRow({ fattura, onMarkFatturata, isSaving }: FatturaRowProps) {
  const [numeroFattura, setNumeroFattura] = useState('');
  const isDaFatturare = fattura.stato === 'DA_FATTURARE';
  const importoEuro = Number(fattura.importoCents) / 100;

  return (
    <tr className="border-b last:border-0" data-fattura-id={fattura.id}>
      <td className="py-3 pr-4 whitespace-nowrap">{DATE_FORMAT.format(new Date(fattura.createdAt))}</td>
      <td className="py-3 pr-4">
        <p className="font-medium">{clienteLabel(fattura)}</p>
        <p className="text-xs text-muted-foreground">
          {fattura.billingSnapshot.piva
            ? `P.IVA ${fattura.billingSnapshot.piva}`
            : fattura.billingSnapshot.codiceFiscale
              ? `CF ${fattura.billingSnapshot.codiceFiscale}`
              : fattura.account.email}
        </p>
      </td>
      <td className="py-3 pr-4">{FATTURA_TIPO_LABELS[fattura.tipo]}</td>
      <td className="py-3 pr-4 whitespace-nowrap">{EURO_FORMAT.format(importoEuro)}</td>
      <td className="py-3 pr-4">
        <span className="text-xs text-muted-foreground" title={fattura.paymentRef}>
          {fattura.paymentRef}
        </span>
      </td>
      <td className="py-3 pr-4">
        <Badge variant={isDaFatturare ? 'outline' : 'default'}>
          {FATTURA_STATO_LABELS[fattura.stato]}
        </Badge>
      </td>
      <td className="py-3 text-right">
        {isDaFatturare ? (
          <div className="flex items-center justify-end gap-2">
            <Input
              value={numeroFattura}
              onChange={(e) => setNumeroFattura(e.target.value)}
              placeholder="Numero fattura (opz.)"
              className="w-40"
              aria-label={`Numero fattura per ${clienteLabel(fattura)}`}
            />
            <Button
              size="sm"
              disabled={isSaving}
              onClick={() =>
                onMarkFatturata(numeroFattura.trim() ? { numeroFattura: numeroFattura.trim() } : {})
              }
            >
              Segna come fatturata
            </Button>
          </div>
        ) : (
          <div className="text-right text-sm text-muted-foreground">
            {fattura.numeroFattura ? <p>N. {fattura.numeroFattura}</p> : null}
            {fattura.fatturataAt ? <p>{DATE_FORMAT.format(new Date(fattura.fatturataAt))}</p> : null}
          </div>
        )}
      </td>
    </tr>
  );
}
